import type {ComposerAttachment} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import type {OpenCopilotChatEvent} from '@github-ui/copilot-chat-events'
import {subscribeAddCopilotChatReference, subscribeOpenCopilotChat} from '@github-ui/copilot-chat-events'
import {
  CopilotChatIntents,
  type CopilotChatEventPayload,
  type CopilotChatReference,
} from '@github-ui/copilot-chat-types'
import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import {createContext, useCallback, useEffect, useMemo, useRef, useState, use, type ReactNode} from 'react'

import {implicitAttachmentIcon, implicitAttachmentKey, implicitAttachmentUrl} from '../references/implicit-context'
import {copilotChatReferenceToAttachment} from '../references/reference-to-attachment'
import {shouldAutoSubmitOpenCopilotChatPayload} from '../references/should-auto-submit-open-chat'
import {readCurrentImplicitContextPage} from '../hooks/use-current-implicit-context-page'

/** Prefix on injected-reference chip ids so the composer can route removals. */
export const INJECTED_ATTACHMENT_PREFIX = 'injected-attachment:'

/** A user-triggered diff reference resolved into a composer chip + AHP attachment. */
export interface InjectedAttachment {
  token: ComposerAttachment
  ahp: MessageAttachment
}

/**
 * The volatile injected-reference data. Split from the stable actions (below) so
 * action-only consumers don't re-render on every reference change, per the
 * `no-mixed-stability-context-value` guidance.
 */
interface InjectedReferencesData {
  /** Attachments injected via the shared diff-view affordances, newest last. */
  injected: InjectedAttachment[]
  /**
   * Whether the page's implicit context should be suppressed for the currently
   * injected references. Set when an `open-copilot-chat` event carries
   * `skipImplicitReferences: true` (the diff-header "Ask Copilot" paths do this),
   * mirroring immersive, which then attaches only the requested diff and omits
   * the page's implicit attachment. Resets once the injected references clear.
   */
  skipImplicitReferences: boolean
  /** `explainError` marks a prompt auto-submitted from "Explain error" on a failed Actions job. */
  pendingAutoSubmit: {id: number; prompt: string; pageUrl: string | null; explainError?: true} | null
  submitInFlight: boolean
}

/** The stable action callbacks for mutating injected references. */
interface InjectedReferencesActions {
  /** Removes a single injected attachment by its chip id (user dismissal). */
  removeInjected: (id: string) => void
  /** Drops the injected attachments with the given chip ids (e.g. after submit). */
  clearInjected: (ids: string[]) => void
  /**
   * Claim/release/consume a pending prompt. The claim lives here rather than in the
   * composer because the token outlives composer remounts, so a panel that closes
   * mid-dispatch would otherwise let the next composer send the same prompt again.
   */
  startPendingAutoSubmit: (id: number) => boolean
  releasePendingAutoSubmit: (id: number) => void
  startAutoSubmitHandoff: (id: number) => boolean
  beginSubmit: () => () => void
  completePendingAutoSubmit: (id: number) => void
}

const InjectedReferencesDataContext = createContext<InjectedReferencesData>({
  injected: [],
  skipImplicitReferences: false,
  pendingAutoSubmit: null,
  submitInFlight: false,
})

const InjectedReferencesActionsContext = createContext<InjectedReferencesActions>({
  removeInjected: () => {},
  clearInjected: () => {},
  startPendingAutoSubmit: () => false,
  releasePendingAutoSubmit: () => {},
  startAutoSubmitHandoff: () => false,
  beginSubmit: () => () => {},
  completePendingAutoSubmit: () => {},
})

export function isPendingAutoSubmitCurrent(pending: {pageUrl: string | null} | null): boolean {
  return pending !== null && pending.pageUrl === (readCurrentImplicitContextPage()?.url ?? null)
}

/** Reads the volatile injected-reference data (re-renders on reference changes). */
export function useInjectedReferencesData(): InjectedReferencesData {
  return use(InjectedReferencesDataContext)
}

/** Reads the stable injected-reference action callbacks (never re-renders on data changes). */
export function useInjectedReferencesActions(): InjectedReferencesActions {
  return use(InjectedReferencesActionsContext)
}

/** Resolves a chat reference into an injected attachment, or `null` if unsupported. */
function referenceToInjectedAttachment(ref: CopilotChatReference): InjectedAttachment | null {
  const ahp = copilotChatReferenceToAttachment(ref)
  if (!ahp) return null
  return {
    token: {
      id: `${INJECTED_ATTACHMENT_PREFIX}${crypto.randomUUID()}`,
      label: ahp.label ?? '',
      icon: implicitAttachmentIcon(ahp),
      url: implicitAttachmentUrl(ahp),
    },
    ahp,
  }
}

/**
 * Captures references published by the shared diff-view affordances
 * (`@github-ui/copilot-code-chat`, over `@github-ui/copilot-chat-events`) and
 * exposes them to the composer as removable attachments — giving quick chat the
 * same explicit diff-attach affordance as immersive Copilot Chat.
 *
 * Mounted above the (conditionally rendered) chat panel so a reference attached
 * while the panel is closed is retained and surfaces when it opens.
 * `onReferencesReceived` lets the host open the panel in response, mirroring the
 * `openPanel` intent of `publishAddCopilotChatReference`.
 *
 * `enabled` gates only the window subscriptions: quick chat coexists with the old
 * immersive embedded chat (both are mounted together by the nav loader), and
 * immersive may already own those live events. A `pendingOpenEvent` is different:
 * the loader has explicitly routed it to this provider and withheld it from
 * immersive, so it must be processed regardless of live-event ownership.
 */
export function InjectedReferencesProvider({
  enabled = true,
  onReferencesReceived,
  pendingOpenEvent,
  onPendingOpenEventHandled,
  children,
}: {
  enabled?: boolean
  onReferencesReceived?: () => void
  /**
   * The `open-copilot-chat` event to process. The global nav records every such
   * event (`CopilotItems.tsx:60-77`), so for auto-submit this is the normal
   * transport, not just cold-start recovery for events the live listener missed.
   */
  pendingOpenEvent?: OpenCopilotChatEvent | null
  onPendingOpenEventHandled?: () => void
  children: ReactNode
}) {
  const [state, setState] = useState<InjectedReferencesData>({
    injected: [],
    skipImplicitReferences: false,
    pendingAutoSubmit: null,
    submitInFlight: false,
  })
  const nextAutoSubmitIdRef = useRef(0)

  const addReferences = useCallback(
    (
      refs: readonly CopilotChatReference[],
      openPanel: boolean,
      skipImplicit = false,
      replace = false,
      autoSubmitPrompt?: string,
      explainError = false,
    ) => {
      // Resolve before touching state so the updater stays pure (dev double-invokes
      // it) and we can decide whether to surface the panel from the caller.
      const resolved: InjectedAttachment[] = []
      for (const ref of refs) {
        const attachment = referenceToInjectedAttachment(ref)
        if (attachment) resolved.push(attachment)
      }
      const pendingAutoSubmit =
        autoSubmitPrompt === undefined
          ? undefined
          : {
              id: ++nextAutoSubmitIdRef.current,
              prompt: autoSubmitPrompt,
              pageUrl: readCurrentImplicitContextPage()?.url ?? null,
              ...(explainError ? {explainError: true as const} : {}),
            }
      if (resolved.length === 0 && !replace && !pendingAutoSubmit) return

      setState(prev => {
        // A new-thread or auto-submit event (`replace`) starts a clean slate: only
        // the requested references are attached, so pending injected state from a
        // previous intent is not submitted alongside it.
        const base = replace ? [] : prev.injected
        const existingKeys = new Set(base.map(item => implicitAttachmentKey(item.ahp)))
        const additions = resolved.filter(item => {
          const key = implicitAttachmentKey(item.ahp)
          if (existingKeys.has(key)) return false
          existingKeys.add(key)
          return true
        })
        const injected = replace ? additions : additions.length > 0 ? [...prev.injected, ...additions] : prev.injected
        // Enable skip-implicit alongside the injected diff. A new thread resets to
        // the incoming intent; otherwise keep it on once set.
        const skipImplicitReferences = replace
          ? resolved.length > 0
            ? skipImplicit
            : false
          : prev.skipImplicitReferences || skipImplicit
        // A plain `newThread` discards any pending prompt: the user asked for a fresh
        // conversation. Remount-retry is unaffected because it never replaces.
        const nextPendingAutoSubmit = pendingAutoSubmit ?? (replace ? null : prev.pendingAutoSubmit)
        if (
          !replace &&
          injected === prev.injected &&
          skipImplicitReferences === prev.skipImplicitReferences &&
          nextPendingAutoSubmit === prev.pendingAutoSubmit
        ) {
          return prev
        }
        return {...prev, injected, skipImplicitReferences, pendingAutoSubmit: nextPendingAutoSubmit}
      })
      // Only surface the panel when the caller asked for it. `add-copilot-chat-reference`
      // defaults `openPanel` to false (attach without opening); `open-copilot-chat`
      // always intends to open. This mirrors immersive's handling of the event.
      if (openPanel) onReferencesReceived?.()
    },
    [onReferencesReceived],
  )

  // Discard all pending injected diffs (and the skip-implicit intent) so a fresh
  // conversation starts clean. Used for reference-less new-thread events, where
  // there is nothing to add but stale state must still be cleared. A pending
  // auto-submit prompt is dropped too, for the same reason.
  const resetInjected = useCallback(() => {
    setState(prev =>
      prev.injected.length === 0 && !prev.skipImplicitReferences && prev.pendingAutoSubmit === null
        ? prev
        : {...prev, injected: [], skipImplicitReferences: false, pendingAutoSubmit: null},
    )
  }, [])

  // Applies an `open-copilot-chat` payload's references to the injected state.
  // Shared by the live window subscription and the pending-event effect so a
  // diff-reference event is handled identically whether it arrives before or
  // after this provider's subscription is set up.
  const handleOpenEventPayload = useCallback(
    (payload: CopilotChatEventPayload | undefined, autoSubmitPrompt?: string) => {
      const refs = payload?.references
      // A `newThread` event opens a fresh conversation, so replace any pending
      // injected diff with only the newly requested one — mirroring immersive's
      // new-thread handling and honoring the "only the requested diff" intent.
      const newThread = !!payload && 'newThread' in payload && payload.newThread === true
      // An auto-submit event starts from a clean slate even without `newThread`: a
      // stale `skipImplicitReferences` from an earlier diff click would otherwise
      // suppress implicit resolution and submit that diff instead of this page's
      // own context. The provider outlives soft navigation, so it does carry over.
      const replace = newThread || autoSubmitPrompt !== undefined
      const explainError = autoSubmitPrompt !== undefined && payload?.intent === CopilotChatIntents.actionsAgent
      if (!refs || refs.length === 0) {
        // A reference-less new-thread event (e.g. the global-nav "new
        // conversation" button) still starts a clean slate. This provider lives
        // outside the remounted chat subtree, so its state survives; discard any
        // pending injected diff here or it would be submitted in the new thread.
        // A plain open event carries no such intent and leaves state untouched.
        if (newThread && autoSubmitPrompt === undefined) resetInjected()
        if (autoSubmitPrompt !== undefined) addReferences([], false, false, replace, autoSubmitPrompt, explainError)
        return
      }
      // Preserve the payload's skip-implicit intent (only `open-copilot-chat`
      // carries it; the diff-header "Ask Copilot" paths set it). Mirrors
      // immersive's `CopilotChatManagerContext`, which skips fetching the page's
      // implicit references so only the requested diff is attached. Gated on refs
      // so we never suppress implicit context without a diff attached.
      const skip = !!payload && 'skipImplicitReferences' in payload && payload.skipImplicitReferences === true
      addReferences(refs, true, skip, replace, autoSubmitPrompt, explainError)
    },
    [addReferences, resetInjected],
  )

  useEffect(() => {
    if (!enabled) return
    const unsubscribeAdd = subscribeAddCopilotChatReference(event =>
      addReferences([event.reference], event.openPanel ?? false),
    )
    const unsubscribeOpen = subscribeOpenCopilotChat(event => {
      handleOpenEventPayload(event.payload)
    })
    return () => {
      unsubscribeAdd()
      unsubscribeOpen()
    }
  }, [enabled, addReferences, handleOpenEventPayload])

  // Process the host-recorded event once, deferred so we don't setState during the
  // effect, then let the host clear it. The loader explicitly routes this event
  // here and withholds it from immersive, independently of live-event ownership.
  useEffect(() => {
    if (!pendingOpenEvent) return
    const payload = pendingOpenEvent.payload
    const autoSubmitPrompt = payload && shouldAutoSubmitOpenCopilotChatPayload(payload) ? payload.content : undefined
    let cancelled = false
    queueMicrotask(() => {
      if (cancelled) return
      handleOpenEventPayload(payload, autoSubmitPrompt)
      onReferencesReceived?.()
      onPendingOpenEventHandled?.()
    })
    return () => {
      cancelled = true
    }
  }, [pendingOpenEvent, handleOpenEventPayload, onReferencesReceived, onPendingOpenEventHandled])

  // Reset the skip-implicit intent when the last injected reference clears, so
  // the next message uses the page's implicit context again.
  const removeInjected = useCallback((id: string) => {
    setState(prev => {
      const injected = prev.injected.filter(item => item.token.id !== id)
      if (injected.length === prev.injected.length) return prev
      return {
        ...prev,
        injected,
        skipImplicitReferences: injected.length === 0 ? false : prev.skipImplicitReferences,
      }
    })
  }, [])

  const clearInjected = useCallback((ids: string[]) => {
    if (ids.length === 0) return
    const removed = new Set(ids)
    setState(prev => {
      const injected = prev.injected.filter(item => !removed.has(item.token.id))
      if (injected.length === prev.injected.length) return prev
      return {
        ...prev,
        injected,
        skipImplicitReferences: injected.length === 0 ? false : prev.skipImplicitReferences,
      }
    })
  }, [])

  // A ref keeps check-and-set synchronous, so two composers mounting in the same
  // tick cannot both win the claim. Only one prompt is ever pending.
  const claimedAutoSubmitIdRef = useRef<number | null>(null)

  const startPendingAutoSubmit = useCallback((id: number) => {
    if (claimedAutoSubmitIdRef.current === id) return false
    claimedAutoSubmitIdRef.current = id
    return true
  }, [])

  const activeSubmitsRef = useRef(0)
  const beginSubmit = useCallback(() => {
    activeSubmitsRef.current += 1
    setState(prev => (prev.submitInFlight ? prev : {...prev, submitInFlight: true}))
    let ended = false
    return () => {
      if (ended) return
      ended = true
      activeSubmitsRef.current -= 1
      if (activeSubmitsRef.current > 0) return
      setState(prev => (prev.submitInFlight ? {...prev, submitInFlight: false} : prev))
    }
  }, [])

  const handedOffAutoSubmitIdRef = useRef<number | null>(null)
  const startAutoSubmitHandoff = useCallback((id: number) => {
    if (handedOffAutoSubmitIdRef.current === id) return false
    handedOffAutoSubmitIdRef.current = id
    return true
  }, [])

  const releasePendingAutoSubmit = useCallback((id: number) => {
    if (claimedAutoSubmitIdRef.current !== id) return
    claimedAutoSubmitIdRef.current = null
    // Re-arm by identity so the effect re-runs in whichever composer is now mounted.
    setState(prev =>
      prev.pendingAutoSubmit?.id === id ? {...prev, pendingAutoSubmit: {...prev.pendingAutoSubmit}} : prev,
    )
  }, [])

  const completePendingAutoSubmit = useCallback((id: number) => {
    if (claimedAutoSubmitIdRef.current === id) claimedAutoSubmitIdRef.current = null
    setState(prev => (prev.pendingAutoSubmit?.id === id ? {...prev, pendingAutoSubmit: null} : prev))
  }, [])

  const actions = useMemo<InjectedReferencesActions>(
    () => ({
      removeInjected,
      clearInjected,
      startPendingAutoSubmit,
      releasePendingAutoSubmit,
      startAutoSubmitHandoff,
      beginSubmit,
      completePendingAutoSubmit,
    }),
    [
      removeInjected,
      clearInjected,
      startPendingAutoSubmit,
      releasePendingAutoSubmit,
      startAutoSubmitHandoff,
      beginSubmit,
      completePendingAutoSubmit,
    ],
  )

  return (
    <InjectedReferencesDataContext value={state}>
      <InjectedReferencesActionsContext value={actions}>{children}</InjectedReferencesActionsContext>
    </InjectedReferencesDataContext>
  )
}
